import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
	useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
	Dropdown,
	DropdownHeader,
	Form,
	Label
} from 'semantic-ui-react';

import {
	log,
	sortArrayByReference
} from '../../services/utils';

import DescriptionTooltip from './DescriptionTooltip';
import FormInput from './FormInput';
import HelpModal from '../Shared/HelpModal';
import ValidationPopup from './ValidationPopup';

import styles from './form.module.css';

const FormSelect = ( {
	align = 'left',
	allowAdditions,
	errors = [],
	fieldLabel,
	forceValidation,
	helpId,
	instructions,
	multiple,
	name,
	onAddItem,
	onChange,
	options,
	optionGroups,
	readOnly,
	renderLabel,
	required,
	search,
	trigger,
	value,
	width,
	...props
} ) => {
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ validationPopup, setValidationPopup ] = useState( false );

	// when search is true, need to target the first child of inputRef.current
	const hasFocus = useCallback(
		() => search
			? inputRef.current?.childNodes[ 0 ]
			: inputRef.current === document.activeElement,
		[ search ]
	);
	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);

	useEffect( () => {
		if ( !readOnly ) {
			inputRef.current.childNodes[ 0 ].setAttribute( 'autoComplete', 'chrome-off' );
		}
	}, [ readOnly ] );

	const handleChange = useCallback( (
		e,
		{
			name,
			value
		}
	) => {
		// disable validationPopup while user is entering a value
		// disable validation while user is entering a value
		if ( validate ) {
			setValidate( false );
		}

		if ( validationPopup ) {
			setValidationPopup( false );
		}

		let val = value;

		if ( Array.isArray( value ) ) {
			const referenceArray = options.map( ( { value } ) => value );

			val = sortArrayByReference( value, referenceArray );
		}

		onChange( {
			target: {
				name,
				value: val
			}
		} );
	}, [
		onChange,
		options,
		validate,
		validationPopup
	] );

	const handleFocus = useCallback( () => {
		if (
			!validationPopup
			&& isInvalid()
			&& hasFocus()
		) {
			setValidationPopup( true );
		} else if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		hasFocus,
		isInvalid,
		validationPopup
	] );

	const handleClose = useCallback( () => document.activeElement.blur(), [] );

	const handleBlur = useCallback( () => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		// disable validationPopup when field loses focus
		if ( validationPopup ) {
			setValidationPopup( false );
		}
	}, [
		errors.length,
		validate,
		validationPopup
	] );

	// const optionsArr = useMemo( () => {
	// 	if ( optionGroups ) {
	// 		return [ ...options ];
	// 	}

	// 	return [ ...options ];
	// }, [ optionGroups, options ] );

	const optionsArr = useMemo( () => {
		if ( optionGroups ) {
			return optionGroups.reduce( (
				acc,
				{
					icon,
					label,
					optionIds
				}
			) => {
				return [
					...acc,
					{
						key: label,
						content: <DropdownHeader inert><i className={ styles.headerIcon } dangerouslySetInnerHTML={{ __html: icon }} />{label}</DropdownHeader>,
						className: styles.dropdownHeader
						// style: {
						// 	pointerEvents: 'none',
						// 	cursor: 'default',
						// 	borderBottom: '1px solid rgba(34,36,38,0.1)',
						// 	borderTop: '1px solid rgba(34,36,38,0.1)',
						// 	padding: '0.5em 0',
						// 	marginBottom: '0.5em',
						// 	lineHeight: 2,
						// 	fontWeight: 'bold'
						// }
					},
					...optionIds.map( id => {
						const option = options.find( option => option.value === id );

						if ( !option ) {
							return null;
						}

						return option;
					} )
				];
			}, [] );
		}

		return [ ...options ];
	}, [ optionGroups, options ] );

	const readOnlyValue = useMemo( () => {
		if ( !value || !options.length ) {
			return '';
		}

		if ( Array.isArray( value ) ) { // for multiple selection
			return options.reduce(
				( acc, o ) => value.includes( o.value )
					? [
						...acc,
						renderLabel
							? <Label
								key={ o.text }
								size="tiny"
								{ ...renderLabel( o ) }
							/>
							: o.text
					]
					: acc,
				[]
			);
		}

		return options.find( o => o.value === value )
			? options.find( o => o.value === value ).text
			: '';
	}, [
		options,
		renderLabel,
		value
	] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>
			{ log( `- ${ name } select` ) }
			{ readOnly
				? Array.isArray( readOnlyValue ) // for multiple selection
					? <Form.Field>
						<label>
							{ helpId
								? <HelpModal
									id={ helpId }
									triggerPosition="left"
									triggerSize="small"
								/>
								: null
							}
							{ fieldLabel }
							{ instructions
								? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></>
								: null
							}
						</label>
						<div>
							{ readOnlyValue.length
								? readOnlyValue
								: '--'
							}
						</div>
					</Form.Field>
					: trigger
						? <Form.Field>
							<label>
								{ helpId
									? <HelpModal
										id={ helpId }
										triggerPosition="left"
										triggerSize="small"
									/>
									: null
								}
								{ fieldLabel }
								{ instructions
									? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></>
									: null
								}
							</label>
							{ trigger }
						</Form.Field>
						: <FormInput
							fieldLabel={ fieldLabel }
							helpId={ helpId }
							name={ name }
							readOnly
							value={ readOnlyValue || '--' }
							width={ width }
						/>
				: <>
					<Form.Field
						error={ isInvalid() }
						required={ required }
						style={ alignment }
						width={ width }
					>
						<label>
							{ helpId
								? <HelpModal
									id={ helpId }
									triggerPosition="left"
									triggerSize="small"
								/>
								: null
							}
							{ fieldLabel }
							{ instructions
								? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></>
								: null
							}
						</label>
						{ /* !!!! removed the Ref component and added ref prop */ }
						<Dropdown
							allowAdditions={ allowAdditions }
							fluid
							multiple={ multiple }
							name={ name }
							onAddItem={ onAddItem }
							onBlur={ handleBlur }
							onChange={ handleChange }
							onClose={ handleClose }
							onFocus={ handleFocus }
							options={ optionsArr }
							ref={ inputRef }
							renderLabel={ renderLabel }
							search={ search }
							selection
							selectOnBlur={ false }
							trigger={ trigger }
							value={ value }
							{ ...props }
						/>
					</Form.Field>
					<ValidationPopup
						context={ inputRef }
						errors={ errors }
						open={ validationPopup }
					/>
				</>
			}
		</>
	);
};

FormSelect.propTypes = {
	align: PropTypes.string,
	allowAdditions: PropTypes.bool,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node.isRequired,
	forceValidation: PropTypes.bool,
	helpId: PropTypes.number,
	instructions: PropTypes.string,
	multiple: PropTypes.bool,
	name: PropTypes.string.isRequired,
	onAddItem: PropTypes.func,
	onChange: PropTypes.func,
	options: PropTypes.arrayOf( PropTypes.object ).isRequired,
	optionGroups: PropTypes.array,
	readOnly: PropTypes.bool,
	renderLabel: PropTypes.func,
	required: PropTypes.bool,
	search: PropTypes.bool,
	trigger: PropTypes.node,
	value: PropTypes.oneOfType( [
		PropTypes.string,
		PropTypes.number,
		PropTypes.array
	] ),
	width: PropTypes.number
};

export default memo( FormSelect );
